/**
 * CSV export utility with RFC 4180 escaping and UTF-8 BOM.
 * BOM ensures Arabic text renders correctly when opened in Excel.
 */

export function escapeCSV(value) {
  let str = String(value ?? '');
  // A cell that is WHOLLY a numeric literal (incl. negative/decimal) is never a
  // spreadsheet formula — don't formula-guard it (keeps grouped-CSV byte-parity
  // with the server, which emits comparison numbers raw: `…,-3,-38`). A DDE
  // payload like `-2+3+cmd|'/c calc'!A0` is never a pure number, so this
  // exemption is injection-safe — it must be a full-string match, NOT a prefix
  // regex like /^[+-](\d|\.\d)/ which would wrongly exempt that payload.
  const isPlainNumber = /^-?(\d+\.?\d*|\.\d+)$/.test(str);
  // Neutralize spreadsheet formula injection: a cell starting with = + - @ (or
  // a leading tab/CR) is treated as a formula by Excel/Sheets. Prefix a single
  // quote so it renders as literal text.
  if (!isPlainNumber && /^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

/**
 * The exact bytes `downloadCSV` writes: UTF-8 BOM, RFC 4180 CRLF terminators,
 * `escapeCSV` per cell. Extracted so a server-side twin can be asserted
 * byte-for-byte against it (see server/src/reports/csv.ts's `gridToCsvText`,
 * pinned from both sides by the Forms report contract fixture) instead of the
 * two agreeing only on the pre-serialization grid.
 */
export function csvText(headers, rows) {
  const headerLine = headers.map(escapeCSV).join(',');
  const dataLines = rows.map((row) => row.map(escapeCSV).join(','));
  return '\uFEFF' + [headerLine, ...dataLines].join('\r\n');
}

export function downloadCSV(headers, rows, filename) {
  downloadCSVText(csvText(headers, rows), filename);
}

/**
 * Save a blob through an <a download> click. Call it from inside a user click: Chrome holds a save that fires seconds
 * after the click (an "automatic download") for permission. The URL is revoked later — revoking at once can cancel the
 * download in some browsers.
 */
export function saveBlob(blob, filename) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

export function downloadCSVText(text, filename) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * A SheetJS workbook from `[{ name, rows, rtl, colWidths?, rowLevels?, outlineAbove? }]`.
 * A cell is a plain value or a SheetJS cell object (`{ t, v, z }`); report dates
 * arrive as Excel serials with a date `z` (orgExcelDate) — a Date would be read
 * in the viewer's own zone (`cellDates`). `rowLevels` (`{ level, hidden? }` per
 * row) becomes the Excel row outline (collapsible +/− groups); `outlineAbove`
 * puts each summary row above its children. RTL lives on the workbook view —
 * the writer ignores a sheet-level flag.
 */
export function buildWorkbook(XLSX, sheets) {
  const wb = XLSX.utils.book_new();
  for (const s of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(s.rows, { cellDates: true });
    if (s.colWidths) ws['!cols'] = s.colWidths.map((wch) => ({ wch }));
    if (s.rowLevels) ws['!rows'] = s.rowLevels.map(({ level, hidden }) => (hidden ? { level, hidden } : { level }));
    if (s.outlineAbove) ws['!outline'] = { above: true };
    XLSX.utils.book_append_sheet(wb, ws, s.name);
  }
  if (sheets.some((s) => s.rtl)) wb.Workbook = { Views: [{ RTL: true }] };
  return wb;
}

/** Write the sheets as an .xlsx download; the `xlsx` module loads on demand. */
export async function downloadXLSX(sheets, filename) {
  const XLSX = await import('xlsx');
  XLSX.writeFile(buildWorkbook(XLSX, sheets), filename);
}
